import React, { FC, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MuiButton from "@mui/material/Button";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import { useAvailableLanguagesSetting } from "@/hooks/useSetting";
import { useCategoryByType } from "@/hooks/useCategories";
import {
  EDUCATIONAL_RESOURCE_TYPES,
  EducationalResource,
  EducationalResourcePayload,
  EducationalResourceType,
} from "@/domain/educationalResources.interface";
import educationalResourceValidationSchema, {
  EducationalResourceFormData,
} from "./validation.schema";

// Change this constant if the category type in your backend differs.
const EDUCATIONAL_RESOURCE_CATEGORY_TYPE = "EDUCATIONAL_RESOURCE";

type EducationalResourceModalProps = {
  open: boolean;
  onClose: () => void;
  educationalResource?: EducationalResource;
  onSave: (data: EducationalResourcePayload) => Promise<void>;
  isPending: boolean;
};

const EMPTY_LANGUAGE = {
  language: "",
  title: "",
  description: "",
  url: "",
};

const DEFAULT_VALUES: EducationalResourceFormData = {
  categoryId: "",
  type: "VIDEO",
  status: "ACTIVE",
  thumbnailUrl: "",
  duration: undefined,
  languages: [EMPTY_LANGUAGE],
};

const pickCategoryLabel = (
  langs: { name: string; language: string }[] | undefined
) => {
  if (!langs || langs.length === 0) return "";
  const en = langs.find((l) => l.language?.toLowerCase().startsWith("en"));
  return (en || langs[0]).name;
};

const EducationalResourceModal: FC<EducationalResourceModalProps> = ({
  open,
  onClose,
  educationalResource,
  onSave,
  isPending,
}) => {
  const { t } = useTranslation("common");
  const theme = useAppTheme();
  const { userInfo } = useUserStore();
  const isEdit = !!educationalResource;

  const availableLanguages = useAvailableLanguagesSetting();
  const { category, isLoadingCategory } = useCategoryByType({
    type: EDUCATIONAL_RESOURCE_CATEGORY_TYPE,
    status: "ACTIVE",
  });

  const languageOptions = useMemo(
    () =>
      availableLanguages.map((code) => ({
        key: code,
        value: code,
      })),
    [availableLanguages]
  );

  const categoryOptions = useMemo(
    () =>
      (category?.categories || []).map((c) => ({
        key: c._id,
        value: pickCategoryLabel(c.languages) || c._id,
      })),
    [category]
  );

  const typeOptions = useMemo(
    () => EDUCATIONAL_RESOURCE_TYPES.map((t) => ({ key: t, value: t })),
    []
  );

  const methods = useForm<EducationalResourceFormData>({
    resolver: yupResolver(educationalResourceValidationSchema(t)),
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "languages",
  });

  useEffect(() => {
    if (educationalResource) {
      reset({
        categoryId: educationalResource.categoryId,
        type: educationalResource.type,
        status: educationalResource.status,
        thumbnailUrl: educationalResource.attributes?.thumbnailUrl || "",
        duration: educationalResource.attributes?.duration,
        languages:
          educationalResource.languages &&
          educationalResource.languages.length > 0
            ? educationalResource.languages.map((l) => ({
                _id: l._id,
                language: l.language,
                title: l.title,
                description: l.description,
                url: l.url,
              }))
            : [EMPTY_LANGUAGE],
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [educationalResource, reset]);

  const onSubmit = async (data: EducationalResourceFormData) => {
    const currentUserId = userInfo?._id as string;
    const attributes: EducationalResourcePayload["attributes"] = {};
    if (data.thumbnailUrl) attributes.thumbnailUrl = data.thumbnailUrl.trim();
    if (data.duration !== undefined && data.duration !== null) {
      attributes.duration = data.duration;
    }

    const payload: EducationalResourcePayload = {
      categoryId: data.categoryId,
      type: data.type as EducationalResourceType,
      status: data.status as EducationalResourcePayload["status"],
      languages: (data.languages || []).map((l) => ({
        ...(l._id ? { _id: l._id } : {}),
        language: l.language,
        title: (l.title || "").trim(),
        description: (l.description || "").trim(),
        url: (l.url || "").trim(),
      })),
      ...(Object.keys(attributes).length > 0 ? { attributes } : {}),
      ...(isEdit ? { updatedBy: currentUserId } : { createdBy: currentUserId }),
    };
    await onSave(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={isEdit ? "Edit Educational Resource" : "New educational resource"}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 640,
        maxHeight: 760,
        maxWidth: 880,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", pt: "12px" }}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="categoryId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={categoryOptions}
                      value={field.value as string}
                      label="Category"
                      id="resource-category"
                      required
                      isLoading={isLoadingCategory}
                      error={!!errors.categoryId}
                      errorMessage={errors.categoryId?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={typeOptions}
                      value={field.value as string}
                      label="Type"
                      id="resource-type"
                      required
                      error={!!errors.type}
                      errorMessage={errors.type?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={[
                        { key: "ACTIVE", value: "Active" },
                        { key: "INACTIVE", value: "Inactive" },
                      ]}
                      value={field.value as string}
                      label="Status"
                      id="resource-status"
                      required
                      error={!!errors.status}
                      errorMessage={errors.status?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="duration"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="resource-duration"
                      label="Duration (seconds)"
                      type="number"
                      variant="outlined"
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === ""
                            ? undefined
                            : Number(e.target.value)
                        )
                      }
                      error={!!errors.duration}
                      helperText={
                        errors.duration ? errors.duration.message : null
                      }
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="thumbnailUrl"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="resource-thumbnail-url"
                      label="Thumbnail URL"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.thumbnailUrl}
                      helperText={
                        errors.thumbnailUrl ? errors.thumbnailUrl.message : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="titleSmall">Languages *</Typography>
                  <MuiButton
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => append(EMPTY_LANGUAGE)}
                  >
                    Add language
                  </MuiButton>
                </Box>
                {typeof errors.languages?.message === "string" && (
                  <Typography
                    variant="bodySmall"
                    sx={{ color: theme.palette.error.main, mb: 1 }}
                  >
                    {errors.languages.message}
                  </Typography>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {fields.map((fieldItem, index) => (
                    <Paper key={fieldItem.id} variant="outlined" sx={{ p: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1.5,
                        }}
                      >
                        <Typography
                          variant="bodyMedium"
                          sx={{ fontWeight: 600 }}
                        >
                          Language #{index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          disabled={fields.length <= 1}
                          onClick={() => remove(index)}
                          aria-label="remove language"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Grid container spacing={"16px"}>
                        <Grid item xs={12} sm={4}>
                          <Controller
                            name={`languages.${index}.language` as const}
                            control={control}
                            render={({ field }) => (
                              <Select
                                handleChange={(value) => field.onChange(value)}
                                data={languageOptions}
                                value={field.value as string}
                                label="Language"
                                id={`resource-lang-${index}`}
                                required
                                error={!!errors.languages?.[index]?.language}
                                errorMessage={
                                  errors.languages?.[index]?.language
                                    ?.message || ""
                                }
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <Controller
                            name={`languages.${index}.title` as const}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                id={`resource-title-${index}`}
                                label="Title"
                                variant="outlined"
                                required
                                value={field.value}
                                onChange={(value) => field.onChange(value)}
                                error={!!errors.languages?.[index]?.title}
                                helperText={
                                  errors.languages?.[index]?.title?.message ||
                                  null
                                }
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Controller
                            name={`languages.${index}.description` as const}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                id={`resource-description-${index}`}
                                label="Description"
                                variant="outlined"
                                required
                                value={field.value}
                                onChange={(value) => field.onChange(value)}
                                error={!!errors.languages?.[index]?.description}
                                helperText={
                                  errors.languages?.[index]?.description
                                    ?.message || null
                                }
                                multiline
                                minRows={2}
                                maxRows={5}
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Controller
                            name={`languages.${index}.url` as const}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                id={`resource-url-${index}`}
                                label="URL"
                                variant="outlined"
                                required
                                value={field.value}
                                onChange={(value) => field.onChange(value)}
                                error={!!errors.languages?.[index]?.url}
                                helperText={
                                  errors.languages?.[index]?.url?.message ||
                                  null
                                }
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default EducationalResourceModal;
